/* eslint no-param-reassign: 0 */

import {
  ApplicationSSOModel,
  applicationModelFields,
  controlTypeConsts,
  samlSubjectNameIdCustomItem,
} from 'client/util/Constants/ApplicationConstants';
import { cloneDeep } from 'lodash';
import { mapGetters } from 'vuex';
import { notify } from 'client/util';
import Constants, { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import DynamicControls from 'client/components/DynamicControls/DynamicControls.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'client/components/Modal/ModalEventBus';

const applicationModel = StoreModelMap[ItemTypeConsts.application];

const { success, error } = Constants;

const {
  JITATTRIBUTE_TYPE_CONSTANT,
  JITATTRIBUTE_TYPE_DB,
  CONSTANT_ATTRIBUTES,
  DATABASE_ATTRIBUTES,
  CONFIG,
} = applicationModelFields;

export default {
  components: {
    DynamicControls,
  },
  data() {
    return {
      configFields: [],
      configValues: [],
    };
  },
  computed: {
    ...mapGetters(applicationModel, [
      'isItemBeingUpdated',
      'isJitEnabled',
      'templateConstantAttributesValue',
    ]),
  },
  methods: {
    addUniqueIdToConstantAttributesChildren(keyInformation) {
      return keyInformation.value.map(x => ({
        ...x, tempId: this.getUniqueID(),
      }));
    },
    addJITDefaultType(jitAttributes) {
      return jitAttributes.map((attribute) => {
        if (!attribute.type) {
          return { ...attribute, type: JITATTRIBUTE_TYPE_DB };
        }
        return attribute;
      });
    },
    dataKeyChanged(key, value, isRootAttribute) {
      let keyInformation;

      if (isRootAttribute) {
        keyInformation = this.item[key];
        this.setItemField({ key, value });
      } else {
        const config = cloneDeep(this.item.config);
        keyInformation = config[key];

        if (ApplicationSSOModel.KEY_VALUE_INDEX_REGEX.test(key)) {
          const matches = key.match(ApplicationSSOModel.KEY_VALUE_INDEX_REGEX);
          const name = matches[1]; // e.g. testKeys[3] => test
          const type = matches[2]; // e.g. testKeys[3] => Keys
          const index = matches[3]; // e.g. testKeys[3] => 3

          switch (type) {
            case ApplicationSSOModel.KEY_VALUE_KEYS_TYPE:
              config[name].value[index].name = value;
              this.setItemField({ key: CONFIG, value: config });
              break;
            case ApplicationSSOModel.KEY_VALUE_VALUES_TYPE:
              config[name].value[index].value = value;
              this.setItemField({ key: CONFIG, value: config });
              break;
            default:
          }
        } else {
          keyInformation.value = cloneDeep(value);
          this.setItemField({ key: CONFIG, value: config });
        }
      }

      return {
        keyInformation,
      };
    },
    getDynamicControlAttributeValue({ name, type, value }) {
      if (!value && type === JITATTRIBUTE_TYPE_CONSTANT) {
        const constantAttributesDictionary = this.templateConstantAttributesValue;
        return (constantAttributesDictionary[name] && constantAttributesDictionary[name].value) || '';
      }
      return value || '';
    },
    getTemplateJitDictionary() {
      const jitTemplateConfig = this.template.jit;
      if (!jitTemplateConfig) return {};

      const jitCustomTemplateConfig = this.addJITDefaultType(
        jitTemplateConfig.attributes || [],
      );

      return jitCustomTemplateConfig
        .reduce((previous, current) => ({
          [current.name]: current,
          ...previous,
        }), {});
    },
    handleAddDynamicControlAttribute(key) {
      const attribute = {
        isCustomAttribute: true,
        mutable: true,
        name: '',
        readOnly: false,
        tempId: String(Date.now()),
        value: '',
      };

      const { value } = this.getConfigValueKey(key);
      value.push(attribute);

      const config = cloneDeep(this.item.config);
      const newValue = config[key].value;
      newValue.push(attribute);

      this.dataKeyChanged(key, newValue);
    },
    handleChangeListItemDynamicControlAttribute({ listItem, key, values }) {
      const { isRootAttribute } = listItem;

      let keyInformationValue;
      if (isRootAttribute) {
        keyInformationValue = cloneDeep(this.item[key]);
      } else {
        const { config } = cloneDeep(this.item);
        keyInformationValue = config[key].value;
      }

      // updated vue template
      Object.assign(values.find(x => x.tempId === listItem.tempId), listItem);
      // update backbone model
      Object.assign(keyInformationValue.find(x => x.tempId === listItem.tempId), listItem);

      this.dataKeyChanged(key, keyInformationValue, isRootAttribute);
    },
    handleItemToggleChangeDynamicControlAttribute(listItem) {
      const jitDictionary = this.getTemplateJitDictionary();
      const isToggle = listItem.toggleInput.value;
      const jitAttr = jitDictionary[listItem.name];
      const config = cloneDeep(this.item.config);
      const key = jitAttr.type === JITATTRIBUTE_TYPE_CONSTANT
        ? CONSTANT_ATTRIBUTES : DATABASE_ATTRIBUTES;
      const attributes = config[key];
      const dynamicControlValue = this.getDynamicControlAttributeValue(jitAttr);
      listItem.valueReadOnly = !listItem.valueReadOnly;
      listItem.value = dynamicControlValue;

      if (isToggle) {
        attributes.value.push({
          ...listItem,
          isCustomAttribute: true,
          mutable: true,
          tempId: listItem.tempId || this.getUniqueID(),
          value: dynamicControlValue,
          valueReadOnly: false,
        });
      } else {
        attributes.value = attributes.value
          .filter(attribute => attribute.tempId !== listItem.tempId);
      }

      this.dataKeyChanged(key, attributes.value);
    },
    hasCustomAttributeSubjectNameIdFeatureFlag() {
      // This to pass karma tests
      if (!this.$hasFeatureFlag) return false;
      if (!this.$featureFlags) return false;
      return this.$hasFeatureFlag(this.$featureFlags.ssoCustomSamlSubjectNameId);
    },
    async confirmJitAttributeToggle(listItem) {
      const isToggle = listItem.toggleInput.value;
      if (isToggle || !this.isJitEnabled) {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        const headerText = 'Disable User Attribute?';
        const kbLink = this.item.learnMore || KbArticleUrls.gettingStartedSaml2SSO;
        const bodyText = 'Disabling this user attribute will disable Just In Time provisioning for this connector. Are you sure you would like to disable this attribute?';

        ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            headerText,
            bodyText,
            handleCancel: reject,
            handleContinue: resolve,
            kbLink,
            mainActionAttention: true,
            showClose: true,
            cancelText: 'cancel',
            continueText: 'confirm',
          },
        });
      });
    },
    handleItemToggleChange(listItem) {
      this.confirmJitAttributeToggle(listItem)
        .then(() => this.handleItemToggleChangeDynamicControlAttribute(listItem))
        .catch(() => {
          const { toggleInput } = listItem;
          // restore vue observable value
          toggleInput.value = true;
        }).finally(() => {
          ModalEventBus.close();
        });
    },
    getConfigValueKey(key) {
      return this.configValues
        .find(value => value.configFieldID === key);
    },
    getConfigFieldKey(key) {
      return this.configFields
        .find(value => value.id === key);
    },
    getDynamicAttributeFieldsComponentName(key) {
      return key === DATABASE_ATTRIBUTES ? 'DatabaseAttributeFields' : 'CustomAttributeFields';
    },
    getUniqueID() {
      return Math.random().toString(36).substr(2, 9);
    },
    handleRemoveDynamicControlAttribute({ key, index }) {
      const { value } = this.getConfigValueKey(key);
      const [attribute] = value.splice(index, 1);

      const config = cloneDeep(this.item.config);
      let keyInformationValue = config[key].value;

      keyInformationValue = keyInformationValue.filter(x => x.tempId !== attribute.tempId);
      this.dataKeyChanged(key, keyInformationValue);
    },
    mapLegacyDynamicControlConfig({ key, field }) {
      const configField = {
        ...field,
        attribute: key,
        defaultValue: field.value,
        displayOptions: null,
        displayType: controlTypeConsts[field.type] || field.type,
        hide: !field.visible,
        id: key,
        name: key,
        namePlaceholder: '',
        valuePlaceholder: '',
      };

      const configValue = {
        configFieldID: key,
        value: field.value,
      };

      return {
        configField,
        configValue,
      };
    },
    reduceLegacyDynamicControlConfig({ previous, current }) {
      const dynamicProperties = previous;
      const [key, field] = current;

      let { configField, configValue } = this.mapLegacyDynamicControlConfig({
        field,
        key,
      });

      ({ configField } = this.parseCheckboxLegacyDynamicControlConfig({
        configField,
      }));

      ({ configField, configValue } = this.parseCustomAttributesLegacyDynamicControlConfig({
        configField,
        configValue,
        key,
      }));

      ({ configField, configValue } = this.parseDropDownLegacyDynamicControlConfig({
        configField,
        configValue,
        key,
      }));

      ({ configField, configValue } = this.parseFileInputLegacyDynamicControlConfig({
        configField,
        configValue,
        key,
      }));

      ({ configField } = this.parseTextInputLegacyDynamicControlConfig({
        configField,
        key,
      }));

      if (this.hasCustomAttributeSubjectNameIdFeatureFlag()) {
        ({ configField, configValue } = this.parseCustomDropDownLegacyDynamicControlConfig({
          configField,
          configValue,
          key,
        }));
      }

      dynamicProperties.configFields.push(configField);
      dynamicProperties.configValues.push(configValue);

      return dynamicProperties;
    },
    parseCheckboxLegacyDynamicControlConfig({ configField }) {
      if (configField.type === 'bool') {
        configField = {
          ...configField,
        };
      }

      return {
        configField,
      };
    },

    parseCustomDropDownLegacyDynamicControlConfig({ configField, configValue, key }) {
      if (key === 'subjectField') {
        configField.displayType = 'customDropdown';
        configField.type = 'customDropdown';
        configField.displayOptions = configField.options
          .map(x => ({ ...x, value: x.text }));
        configField.displayOptions.push({
          text: 'employeeIdentifier',
          value: 'employeeIdentifier',
        });
        configField = {
          ...configField,
          getDisplayValue: () => configField.value,
          handleDropDownValueChange: (value) => {
            configField.value = value;
            this.dataKeyChanged(configField.id, value);
          },
          handleInputChange: (value, validator) => {
            validator.validate(configField.label)
              .then(() => this.dataKeyChanged(key, value));
          },
          separatorIndices: [7],
          customField: samlSubjectNameIdCustomItem,
        };
      }
      return {
        configField,
        configValue,
      };
    },

    parseDropDownLegacyDynamicControlConfig({ configField, configValue }) {
      if (configField.type === 'select') {
        configField.displayOptions = configField.options
          .map(x => ({ ...x, value: x.text }));

        configField = {
          ...configField,
          getDisplayValue: () => configField.value,
          handleValueChange: (value) => {
            configField.value = value;
            this.dataKeyChanged(configField.id, value);
          },
        };
      }

      return {
        configField,
        configValue,
      };
    },

    parseFileInputLegacyDynamicControlConfig({ configField, configValue }) {
      if (configField.type === 'file') {
        const fieldName = configField.label.substring(0, configField.label.length - 1);
        const buttonText = !this.isItemBeingUpdated ? `Upload ${fieldName}` : `Replace ${fieldName}`;
        configField = {
          ...configField,
          buttonText,
          handleValueChange: event => this.fileInputHandleValueChange({ event, configField }),
          accept: configField.accept || '*',
        };
      }

      return {
        configField,
        configValue,
      };
    },

    parseTextInputLegacyDynamicControlConfig({ configField, key }) {
      if (configField.type === 'text') {
        configField = {
          ...configField,
          handleInputChange: (value, validator) => {
            validator.validate(configField.label)
              .then(() => this.dataKeyChanged(key, value));
          },
        };

        if (key === 'groupsAttributeName') {
          configField = {
            ...configField,
            placeholder: 'memberOf',
          };
        }
      }

      return {
        configField,
      };
    },

    parseCustomAttributesLegacyDynamicControlConfig({ configField, configValue, key }) {
      if (configField.type === 'constantAttributes') {
        configField = {
          ...configField,
          buttonText: 'add attribute',
          component: this.getDynamicAttributeFieldsComponentName(key),
          listName: key,
          nameValidation: 'required',
          valueValidation: '',
          handleAddAttribute: () => {
            this.handleAddDynamicControlAttribute(key);
          },
          handleRemoveAttribute: (index) => {
            this.handleRemoveDynamicControlAttribute({ key, index });
          },
          handleChangeListItem: ({ listItem, values }) => {
            this.handleChangeListItemDynamicControlAttribute({ listItem, key, values });
          },
          handleItemToggleChange: (listItem) => {
            this.handleItemToggleChange(listItem);
          },
        };
      }

      return {
        configField,
        configValue,
      };
    },
    readFileInputValue({ input, configField }, customReader) {
      return new Promise((resolve, reject) => {
        // Prepare the reader for persistence of the base64 file...
        const reader = customReader || new FileReader();
        reader.onload = (event) => {
          const rawContent = event.target.result;
          const base64Content = rawContent.substring(rawContent.indexOf(',') + 1);
          resolve(base64Content);
        };

        reader.onerror = () => {
          reject(new Error(`Could not read ${configField.label}`));
        };

        // Validate input file and bind to model...
        if (input.files.length > 0) {
          // If the file is too big, notify the user...
          const { size } = input.files[0]; // bytes
          if (size > 1024 * 1024) {
            reject(new Error('Files uploaded must be smaller than 1 MB.'));
            return;
          }

          // File looks good, read in the contents and bind to model...
          reader.readAsDataURL(input.files[0]);
        }
      });
    },
    fileInputHandleValueChange({ configField, event }) {
      this.readFileInputValue({ input: event.target, configField }).then((value) => {
        event.target.setAttribute('has-changed', true);
        event.target.nextElementSibling.setAttribute('data-name', event.target.files[0].name);
        notify(success, `Successfully read ${configField.label}`);
        this.dataKeyChanged(configField.id, value);
      }, (readError) => {
        notify(error, readError.message);
      });
    },
  },
};
